import {
  DetailsCard,
  CardHeader,
  LayoutTwoOneRatioRightColumn,
  LayoutTwoOneRatioLeftColumn,
  LayoutTwoOneRatio
} from '@hm/fundamental'
import {useCallback, useEffect, useState} from 'react'

import {useLayoutStyles} from '../styles'
import {
  JobsToBeDone,
  JobsToBeDoneInsightDetails,
  JobsToBeDoneTaskWithContext,
  ViewMode
} from '../types'

import {JobsToBeDoneTable} from './JtbdTable'
import {SideCard} from './SideCard'

interface Props {
  plantUpmId: string
  timezone: string
  items: JobsToBeDone[]
  isEditAllowed: boolean
  associatedRole: string
  insightQueryParams?: {fromDate?: string; toDate?: string}
}

export const JtbdCards: React.FC<Props> = ({
  items,
  plantUpmId,
  timezone,
  isEditAllowed,
  associatedRole,
  insightQueryParams
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.tablesOnlyMode)
  const [selectedItem, setSelectedItem] = useState<JobsToBeDoneTaskWithContext | undefined>(
    undefined
  )
  const [selectedInsight, setSelectedInsight] = useState<JobsToBeDoneInsightDetails | undefined>(
    undefined
  )

  // Close the insight side panel when the role changes
  useEffect(() => {
    setViewMode(ViewMode.tablesOnlyMode)
    setSelectedItem(undefined)
    setSelectedInsight(undefined)
  }, [associatedRole])

  const handleInsightClick = useCallback((insight: JobsToBeDoneInsightDetails) => {
    setSelectedInsight(insight)
    setViewMode(ViewMode.insightDetailMode)
  }, [])

  const handleCancel = useCallback(() => {
    setViewMode(ViewMode.tablesOnlyMode)
    setSelectedItem(undefined)
    setSelectedInsight(undefined)
  }, [])

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    setViewMode(mode)
    // Clear selected insight when going back to details mode (timeline)
    if (mode === ViewMode.detailsMode) {
      setSelectedInsight(undefined)
    }
  }, [])

  const {classes} = useLayoutStyles()

  return (
    <LayoutTwoOneRatio>
      <LayoutTwoOneRatioLeftColumn
        className={`${viewMode !== ViewMode.tablesOnlyMode ? '' : classes.fullWidth}`}
      >
        <div className={classes.invisibleScrollbar}>
          {items.map((item) => (
            <DetailsCard data-cy="jobs-to-be-done-card" className="mb-24px" key={item.id}>
              <CardHeader>
                <div
                  style={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                  }}
                >
                  {item.title}
                </div>
              </CardHeader>
              <JobsToBeDoneTable
                timezone={timezone}
                isEditAllowed={isEditAllowed}
                jobsToBeDone={item}
                selectedItem={selectedItem}
                setViewMode={setViewMode}
                setSelectedItem={(data) => {
                  setViewMode(ViewMode.createMode)
                  setSelectedItem(data)
                  setSelectedInsight(undefined)
                }}
              />
            </DetailsCard>
          ))}
        </div>
      </LayoutTwoOneRatioLeftColumn>
      {viewMode !== ViewMode.tablesOnlyMode && (
        <LayoutTwoOneRatioRightColumn>
          <SideCard
            viewMode={viewMode}
            plantUpmId={plantUpmId}
            selectedItem={selectedItem}
            selectedInsight={selectedInsight}
            setViewMode={handleSetViewMode}
            onInsightClick={handleInsightClick}
            timezone={timezone}
            isEditAllowed={isEditAllowed}
            insightQueryParams={insightQueryParams}
            onCancel={handleCancel}
          />
        </LayoutTwoOneRatioRightColumn>
      )}
    </LayoutTwoOneRatio>
  )
}
